import { type DecisionId, Dtos, pluralize } from "@inrev/common";
import { Option } from "@inrev/core";
import { useAdminFetchAccountBondingLines } from "@portal/api/account/admin";
import { type DataNeeded, sortDataNeeded } from "@portal/api/decisioning";
import { useCreateDecision } from "@portal/api/decisioning/admin";
import { type FocusEvent, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { isApiError } from "../../../../../utils/error";
import { typedZodResolver } from "../../../../../utils/form";
import { FormDropdown, FormMoney, FormPercent, FormTextArea } from "../../../../form/fields";
import { cn } from "../../../../lib/utils";
import { Disclosure } from "../../../../patterns/disclosure";
import { PanelSheet } from "../../../../patterns/panel-sheet";
import { Banner, type ButtonColor, Icon, Text } from "../../../../primitives";
import { DECISION_STAGE_OPTIONS, IMPORTANCE_DOT_CLASSES } from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import { type DecisioningSheet, useDecisioningPanel } from "../DecisioningPanelContext";
import { PanelField } from "../PanelField";
import { ProConSnapshotRow } from "../ProConSnapshotRow";

type Request = Dtos.Decisioning.Decision.Create.Request;
type Stage = Dtos.Decisioning.DecisionStage;
type LogSheet = Extract<DecisioningSheet, { kind: "log" }>;

type LogDecisionFormValues = {
	stage: Stage;
	rationale: string;
	supersedesDecisionId: DecisionId | null;
	rate?: number | null;
	singleLimit?: number | null;
	aggregateLimit?: number | null;
};

const TESTID = "log-decision";
const BANNER_ITEM_LIMIT = 3;
const DEFAULT_STAGE: Stage = "initial_submission_follow_up";

const ACTIONS: Record<Stage, { label: string; color: ButtonColor; toast: string }> = {
	approve: { label: "Log approval", color: "confirm", toast: "Logged approval" },
	decline: { label: "Log decline", color: "danger", toast: "Logged decline" },
	close: { label: "Log close", color: "primary", toast: "Logged close" },
	initial_submission_follow_up: {
		label: "Log follow-up",
		color: "primary",
		toast: "Logged follow-up",
	},
	post_submission_follow_up: {
		label: "Log follow-up",
		color: "primary",
		toast: "Logged follow-up",
	},
};

const CORRECTION_ACTION = { label: "Save correction", toast: "Saved correction" };

const SAVE_FAILED_COPY = "Couldn't save this decision. Try again.";
const REJECTION_STATUSES = new Set([400, 409, 422]);

/** A rejected decision names why, e.g. a rule the account fails; anything else reads as a retry. */
const saveFailedCopy = (error: Error) => {
	if (!isApiError(error) || !REJECTION_STATUSES.has(error.statusCode)) {
		return SAVE_FAILED_COPY;
	}
	const reason = error.message.trim();
	if (reason === "") return SAVE_FAILED_COPY;
	return `Couldn't save this decision: ${reason}${/[.!?]$/.test(reason) ? "" : "."}`;
};

const RATIONALE_REQUIRED_COPY: Record<Stage, string> = {
	approve: "An approval needs a rationale.",
	decline: "A decline needs a rationale.",
	close: "A close needs a rationale.",
	initial_submission_follow_up: "A follow-up needs a rationale.",
	post_submission_follow_up: "A follow-up needs a rationale.",
};

/**
 * Money and percent fields reformat on focus, which drops a selection made in the same tick, so the
 * select waits a frame. Chrome's `select()` also focuses the input, so it must only run while the
 * input still has focus: a stale select after tabbing on pulls focus back and the two inputs then
 * trade it every frame. It also skips when typing started first, so fast keystrokes are not replaced.
 */
const selectInputOnFocus = (event: FocusEvent<HTMLDivElement>) => {
	const input = event.target;
	if (!(input instanceof HTMLInputElement)) return;
	let typed = false;
	const markTyped = () => {
		typed = true;
	};
	input.addEventListener("input", markTyped, { once: true });
	requestAnimationFrame(() => {
		input.removeEventListener("input", markTyped);
		if (!typed && input.ownerDocument.activeElement === input) input.select();
	});
};

const isAggregateBelowSingle = (singleLimit: Option<number>, aggregateLimit: Option<number>) =>
	Option.isSome(singleLimit) && Option.isSome(aggregateLimit) && aggregateLimit < singleLimit;

type StatusInput = {
	saveError: Option<string>;
	blocked: Option<string>;
	stage: Stage;
	rationale: string;
	singleLimit: Option<number>;
	aggregateLimit: Option<number>;
};

const statusFor = ({
	saveError,
	blocked,
	stage,
	rationale,
	singleLimit,
	aggregateLimit,
}: StatusInput): { text: string; danger: boolean } => {
	if (Option.isSome(saveError)) return { text: saveError, danger: true };
	if (Option.isSome(blocked)) return { text: `${blocked}.`, danger: true };
	if (stage === "approve" && isAggregateBelowSingle(singleLimit, aggregateLimit)) {
		return { text: "Aggregate limit must be at least the single limit.", danger: true };
	}
	if (rationale.trim() === "") return { text: RATIONALE_REQUIRED_COPY[stage], danger: true };
	return { text: "Adds to History with this snapshot.", danger: false };
};

const OpenItemsBanner = ({ items }: { items: readonly DataNeeded[] }) => {
	const shown = sortDataNeeded(items).slice(0, BANNER_ITEM_LIMIT);
	return (
		<Banner tone="notification" className="items-start" data-testid={`${TESTID}-open-items`}>
			<Banner.Leading className="pt-0.5">
				<Icon type="exclamation-triangle" size="mini" color="warning" />
			</Banner.Leading>
			<Banner.Content className="flex flex-col gap-1">
				<Text type="body-strong" color="primary" className="text-[13px]">
					{`${pluralize(items.length, "requested item is", "requested items are")} still open`}
				</Text>
				<ul className="m-0 flex flex-col gap-0.5 p-0">
					{shown.map((item) => (
						<li key={item.id} className="flex list-none items-center gap-1.5">
							<span
								aria-hidden
								className={cn(
									"size-1.5 shrink-0 rounded-full",
									IMPORTANCE_DOT_CLASSES[item.importance],
								)}
							/>
							<Text type="caption" color="secondary" truncate className="block min-w-0">
								{item.title}
							</Text>
						</li>
					))}
				</ul>
				{items.length > shown.length && (
					<Text type="caption" color="tertiary">{`+${items.length - shown.length} more`}</Text>
				)}
			</Banner.Content>
		</Banner>
	);
};

const SnapshotDisclosure = ({ openItems }: { openItems: readonly DataNeeded[] }) => {
	const { proCons } = useDecisioningData();
	const pros = proCons.filter((item) => item.kind === "pro");
	const cons = proCons.filter((item) => item.kind === "con");

	return (
		<Disclosure testId={`${TESTID}-snapshot`}>
			<Disclosure.Summary testId={`${TESTID}-snapshot-toggle`}>
				{`Snapshot: ${pluralize(pros.length, "pro", "pros")}, ${pluralize(cons.length, "con", "cons")}, ${pluralize(openItems.length, "open item", "open items")}`}
			</Disclosure.Summary>
			<Disclosure.Panel>
				{proCons.length + openItems.length === 0 ? (
					<Disclosure.Row>
						<Text type="caption" color="tertiary">
							Nothing on the board yet.
						</Text>
					</Disclosure.Row>
				) : (
					<>
						{[...pros, ...cons].map((proCon) => (
							<ProConSnapshotRow key={proCon.id} proCon={proCon} />
						))}
						{sortDataNeeded(openItems).map((item) => (
							<Disclosure.Row key={item.id}>
								<Text type="caption" color="warning">
									Open
								</Text>
								<Text type="caption" color="primary" className="min-w-0">
									{item.title}
								</Text>
							</Disclosure.Row>
						))}
					</>
				)}
			</Disclosure.Panel>
		</Disclosure>
	);
};

type ApprovalPrefill = {
	rate: Option<number>;
	singleLimit: Option<number>;
	aggregateLimit: Option<number>;
};

const PREFILL_FIELDS = ["rate", "singleLimit", "aggregateLimit"] as const;

type LogDecisionFormProps = {
	sheet: LogSheet;
	/** Terms from the account's last approved bonding line; arrives after the sheet opens. */
	prefill: ApprovalPrefill;
};

const LogDecisionForm = ({ sheet, prefill }: LogDecisionFormProps) => {
	const { accountId, closeSheet, jumpTo, showToast } = useDecisioningPanel();
	const { proCons, dataNeeded, decisions } = useDecisioningData();
	const { createDecision, createDecisionLoading } = useCreateDecision(accountId);
	const [saveError, setSaveError] = useState<Option<string>>(null);
	const isCorrection = Option.isSome(sheet.supersedesDecisionId);
	const corrected = decisions.find((decision) => decision.id === sheet.supersedesDecisionId);

	const formMethods = useForm<LogDecisionFormValues, unknown, Request>({
		mode: "onTouched",
		defaultValues: {
			stage: sheet.stage ?? corrected?.stage ?? DEFAULT_STAGE,
			rationale: corrected?.rationale ?? "",
			supersedesDecisionId: sheet.supersedesDecisionId ?? null,
			rate: corrected?.rate ?? undefined,
			singleLimit: corrected?.singleLimit ?? undefined,
			aggregateLimit: corrected?.aggregateLimit ?? undefined,
		},
		resolver: typedZodResolver(Dtos.Decisioning.Decision.Create.Request.schema),
	});
	const {
		control,
		trigger,
		handleSubmit,
		getFieldState,
		setValue,
		formState: { isDirty },
	} = formMethods;
	const values = useWatch({ control });
	const stage = values.stage ?? DEFAULT_STAGE;

	/** Fills terms in place once they load, leaving any field the underwriter already edited. */
	useEffect(() => {
		if (isCorrection) return;
		for (const name of PREFILL_FIELDS) {
			const value = prefill[name];
			const field = getFieldState(name);
			if (Option.isSome(value) && !field.isDirty && !field.isTouched) setValue(name, value);
		}
	}, [
		isCorrection,
		prefill.rate,
		prefill.singleLimit,
		prefill.aggregateLimit,
		getFieldState,
		setValue,
	]);

	const aggregateBelowSingle =
		stage === "approve" && isAggregateBelowSingle(values.singleLimit, values.aggregateLimit);

	/** Editing either limit can break or fix the rule, so the aggregate field re-checks itself. */
	useEffect(() => {
		if (aggregateBelowSingle || getFieldState("aggregateLimit").invalid) {
			void trigger("aggregateLimit");
		}
	}, [aggregateBelowSingle, getFieldState, trigger]);

	const openItems = dataNeeded.filter((item) => item.status === "open");
	const blocked = Dtos.Decisioning.decisionBlockedReason(stage, { proCons, dataNeeded });
	const valid = Dtos.Decisioning.Decision.Create.Request.schema.safeParse(values).success;
	const status = statusFor({
		saveError,
		blocked,
		stage,
		rationale: values.rationale ?? "",
		singleLimit: values.singleLimit,
		aggregateLimit: values.aggregateLimit,
	});
	const stageAction = ACTIONS[stage];
	const action = isCorrection ? { ...stageAction, ...CORRECTION_ACTION } : stageAction;
	const showsOpenItems = !Dtos.Decisioning.isFollowUpStage(stage) && openItems.length > 0;

	const submit = handleSubmit((request) => {
		setSaveError(null);
		createDecision(request, {
			onSuccess: (created) => {
				closeSheet();
				jumpTo({ tab: "history", id: created.id });
				showToast(action.toast);
			},
			onError: (error) => setSaveError(saveFailedCopy(error)),
		});
	});

	return (
		<PanelSheet
			testId={`${TESTID}-sheet`}
			title={isCorrection ? "Correct decision" : "Log decision"}
			status={status.text}
			statusTone={status.danger ? "danger" : "default"}
			onCancel={closeSheet}
			dirty={isDirty}
			primaryLabel={action.label}
			primaryColor={action.color}
			primaryDisabled={!valid || Option.isSome(blocked)}
			primaryLoading={createDecisionLoading}
			onPrimary={() => void submit()}
		>
			<PanelField label="Stage" required>
				{({ id, labelId }) => (
					<FormDropdown
						hideErrorMessage
						control={control}
						name="stage"
						options={DECISION_STAGE_OPTIONS}
						buttonId={id}
						aria-labelledby={labelId}
						testId={`${TESTID}-stage`}
					/>
				)}
			</PanelField>
			{stage === "approve" && (
				<div
					className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3.5"
					onFocus={selectInputOnFocus}
				>
					<PanelField label="Rate" required>
						{({ id }) => (
							<FormPercent
								hideErrorMessage
								control={control}
								name="rate"
								decimalPlaces={2}
								max={99.99}
								clearsToNull
								id={id}
								testId={`${TESTID}-rate`}
							/>
						)}
					</PanelField>
					<PanelField label="Single limit" required>
						{({ id }) => (
							<FormMoney
								hideErrorMessage
								control={control}
								name="singleLimit"
								id={id}
								testId={`${TESTID}-single-limit`}
							/>
						)}
					</PanelField>
					<PanelField label="Aggregate limit" required>
						{({ id }) => (
							<FormMoney
								hideErrorMessage
								control={control}
								name="aggregateLimit"
								id={id}
								testId={`${TESTID}-aggregate-limit`}
							/>
						)}
					</PanelField>
				</div>
			)}
			{showsOpenItems && <OpenItemsBanner items={openItems} />}
			<PanelField label="Rationale" required>
				{({ id }) => (
					<FormTextArea
						hideErrorMessage
						control={control}
						name="rationale"
						className="h-[76px]"
						id={id}
						testId={`${TESTID}-rationale`}
					/>
				)}
			</PanelField>
			<SnapshotDisclosure openItems={openItems} />
		</PanelSheet>
	);
};

/**
 * Renders the form straight away and lets the bonding-line terms fill in when they load, so the
 * sheet never remounts under the underwriter mid-typing.
 */
export const LogDecisionSheet = ({ sheet }: { sheet: LogSheet }) => {
	const { accountId } = useDecisioningPanel();
	const { bondingLines } = useAdminFetchAccountBondingLines(accountId);
	const bondingLine = bondingLines?.bondingLine;

	return (
		<LogDecisionForm
			sheet={sheet}
			prefill={{
				rate: bondingLine?.rate,
				singleLimit: bondingLine?.singleLimit,
				aggregateLimit: bondingLine?.aggregateLimit,
			}}
		/>
	);
};
